import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sessionService } from '../../services/session.service';
import { TicketFields, Ticket } from '../../types';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useTicket } from '../../contexts/TicketContext';

const emptyFields: TicketFields = {
  ticketNumber: null,
  plateNumber: null,
  violationDateTime: null,
  violationType: null,
  issuingLocation: null,
  fineAmount: null,
  issuingAgency: null,
  city: null,
  jurisdiction: null,
  ticketCategory: 'other',
};

const TicketReviewPage: React.FC = () => {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialFields = (location.state as any)?.fields as TicketFields | undefined;
  const initialTicket = (location.state as any)?.ticket as Ticket | undefined;
  const { ticketFields, currentTicket, setTicketFields, setSessionFromTicketResponse } = useTicket();
  const hydratedFromStateRef = useRef(false);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!initialFields && !ticketFields);

  useEffect(() => {
    if (initialFields && !hydratedFromStateRef.current) {
      hydratedFromStateRef.current = true;
      setSessionFromTicketResponse({
        ticket: initialTicket,
        fields: initialFields,
        ticketId: initialTicket?.id ?? ticketId ?? null,
      });
      setLoading(false);
    } else if (ticketFields) {
      setLoading(false);
    } else if (ticketId) {
      const fetchTicket = async () => {
        try {
          const resp = await sessionService.getTicket(ticketId);
          if (resp.success && resp.data) {
            setSessionFromTicketResponse({
              ticket: resp.data.ticket,
              fields: resp.data.fields || emptyFields,
              ticketId,
            });
          } else {
            throw new Error(resp.message || 'Failed to load ticket');
          }
        } catch (error: any) {
          toast.error(error.message || 'Failed to load ticket');
        } finally {
          setLoading(false);
        }
      };
      fetchTicket();
    }
  }, [initialFields, initialTicket, ticketFields, ticketId, setSessionFromTicketResponse]);

  const fields = ticketFields || initialFields || emptyFields;
  const activeTicket = currentTicket || initialTicket;

  const handleChange = (key: keyof TicketFields, value: string) => {
    setTicketFields({
      ...fields,
      [key]: value === '' ? null : value,
    });
  };

  const handleNumberChange = (key: keyof TicketFields, value: string) => {
    if (value === '') {
      setTicketFields({ ...fields, [key]: null });
      return;
    }
    const parsed = parseFloat(value);
    setTicketFields({
      ...fields,
      [key]: Number.isFinite(parsed) ? parsed : fields[key],
    });
  };

  const onSubmit = async () => {
    if (!ticketId) {
      toast.error('Missing ticket id');
      return;
    }
    setSaving(true);
    try {
      const response = await sessionService.updateTicketFields(ticketId, fields);
      if (response.success && response.data) {
        toast.success('Ticket updated');
        setSessionFromTicketResponse({
          ticket: response.data.ticket,
          fields: response.data.fields,
          ticketId,
        });
        navigate(`/ticket/${ticketId}/evidence`, { state: { ticket: response.data.ticket, fields: response.data.fields } });
      } else {
        throw new Error(response.message || 'Failed to update ticket');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const title = activeTicket?.id ? `Review Ticket #${activeTicket.id.slice(-6)}` : 'Review Ticket';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading ticket..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h1>
          <p className="text-gray-600 mb-6">Review and adjust the extracted fields before finalizing.</p>

          <div className="space-y-4">
            <FieldInput
              label="Ticket Number"
              value={fields.ticketNumber || ''}
              onChange={(v) => handleChange('ticketNumber', v)}
            />
            <FieldInput
              label="Plate Number"
              value={fields.plateNumber || ''}
              onChange={(v) => handleChange('plateNumber', v)}
            />
            <FieldInput
              label="Violation Date/Time (ISO8601)"
              value={fields.violationDateTime || ''}
              onChange={(v) => handleChange('violationDateTime', v)}
              placeholder="2025-06-21T22:11:00Z"
            />
            <FieldInput
              label="Violation Type"
              value={fields.violationType || ''}
              onChange={(v) => handleChange('violationType', v)}
            />
            <FieldInput
              label="Issuing Location"
              value={fields.issuingLocation || ''}
              onChange={(v) => handleChange('issuingLocation', v)}
            />
            <FieldInput
              label="Fine Amount"
              type="number"
              value={fields.fineAmount !== null && fields.fineAmount !== undefined ? String(fields.fineAmount) : ''}
              onChange={(v) => handleNumberChange('fineAmount', v)}
            />
            <FieldInput
              label="Issuing Agency"
              value={fields.issuingAgency || ''}
              onChange={(v) => handleChange('issuingAgency', v)}
            />
            <FieldInput
              label="City"
              value={fields.city || ''}
              onChange={(v) => handleChange('city', v)}
            />
            <FieldInput
              label="Jurisdiction"
              value={fields.jurisdiction || ''}
              onChange={(v) => handleChange('jurisdiction', v)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Category</label>
              <select
                value={fields.ticketCategory || 'other'}
                onChange={(e) => handleChange('ticketCategory', e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="municipal_parking">Municipal Parking</option>
                <option value="private_parking">Private Parking</option>
                <option value="speeding">Speeding</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={onSubmit}
              disabled={saving}
              className="btn-primary px-6 py-3 text-lg disabled:opacity-60"
            >
              {saving ? <div className="flex items-center space-x-2"><LoadingSpinner size="sm" /><span>Saving...</span></div> : 'Save & Continue to Q&A'}
            </button>
          </div>
        </div>

        {/* Q&A entry point */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">证据问答</h2>
          <p className="text-gray-600 mb-4">保存后进入问答，补充证据以生成更有力的申诉信。</p>
          <button
            onClick={() => navigate(`/ticket/${ticketId}/evidence`, { state: { ticket: activeTicket, fields } })}
            className="btn-secondary"
          >
            进入问答
          </button>
        </div>
      </div>
    </div>
  );
};

const FieldInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
    />
  </div>
);

export default TicketReviewPage;
